import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, ClipboardList, Truck, ShoppingBag, MapPin, Receipt, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/contexts/StoreContext";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const { etaMinutes } = useStore();

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  if (!order) return <div className="max-w-2xl mx-auto p-8"><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="order-success-page">
      {/* Hero success block */}
      <div className="text-center">
        <div className="relative w-24 h-24 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto slide-up-in">
          <div className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" style={{ animationDuration: "1.6s" }} />
          <CheckCircle2 className="w-12 h-12 relative" strokeWidth={2.5} />
        </div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold mt-5 tracking-tight">Order placed!</h1>
        <p className="text-muted-foreground mt-1.5 text-sm md:text-base">
          Thank you — Babu Store will pack this shortly.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5" data-testid="order-number-chip">
          <ClipboardList className="w-4 h-4 text-muted-foreground" />
          <span className="font-mono font-semibold text-sm">{order.order_number}</span>
        </div>
      </div>

      {/* Delivery ETA banner — premium */}
      <div className="mt-6 rounded-2xl bg-gradient-to-r from-primary to-[hsl(var(--brand-red-deep))] text-primary-foreground p-4 md:p-5 flex items-center gap-4 slide-up-in" data-testid="success-eta">
        <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
          <Truck className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">Expected delivery</div>
          <div className="text-lg md:text-xl font-display font-semibold leading-tight">~{etaMinutes || 15} minutes</div>
          <div className="text-[11px] opacity-90 mt-0.5">Live updates will land on your orders page.</div>
        </div>
        <Sparkles className="w-5 h-5 opacity-80 hidden sm:block" />
      </div>

      {/* Details card */}
      <Card className="border bg-card mt-5">
        <CardContent className="p-4 md:p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" />Delivery to</div>
              <div className="mt-1 font-semibold">{order.address?.name}</div>
              <div className="text-muted-foreground text-[13px]">{order.address?.line1}</div>
              <div className="text-muted-foreground text-[13px]">{order.address?.city}, {order.address?.state} — {order.address?.pincode}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Receipt className="w-3 h-3" />Payment</div>
              <div className="mt-1 uppercase font-semibold">{order.payment?.method}</div>
              <div className="text-muted-foreground text-xs">{order.payment?.status}</div>
              <div className="text-xs text-muted-foreground mt-2">Placed at {fmtDate(order.created_at)}</div>
            </div>
          </div>

          <div className="border-t pt-3 space-y-1.5 text-[13px]">
            <div className="flex justify-between"><span className="text-muted-foreground">Items</span><span>{order.items?.length}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{money(order.pricing?.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">GST &amp; taxes</span><span className="tabular-nums">{money(order.pricing?.tax)}</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delivery</span>
              <span className="tabular-nums">{Number(order.pricing?.delivery_fee) === 0 ? <span className="text-emerald-700 font-semibold">FREE</span> : money(order.pricing?.delivery_fee)}</span>
            </div>
            <div className="flex justify-between text-lg md:text-xl font-display font-semibold pt-2 mt-2 border-t"><span>Total paid</span><span className="tabular-nums">{money(order.pricing?.total)}</span></div>
          </div>

          <div className="rounded-xl bg-secondary/60 border px-3 py-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" />
            Secure order · GST invoice will be emailed on dispatch.
          </div>
        </CardContent>
      </Card>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <Button asChild size="lg" variant="outline" data-testid="success-continue-shopping">
          <Link to="/shop"><ShoppingBag className="w-4 h-4 mr-2" />Continue shopping</Link>
        </Button>
        <Button asChild size="lg" className="btn-primary-glow" data-testid="success-track-order">
          <Link to={`/account/orders/${order.id}`}>Track order</Link>
        </Button>
      </div>
    </div>
  );
}
